import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { library, users } from '@cwos/api-client';
import { Button } from '@cwos/ui-admin';
import { formatInstantDay } from './libraryLogic.js';

/**
 * DRAFT RULES WITH PHIBIAN (owner ruling, 2026-10-07).
 *
 * Phibian reads the whole document and drafts readings, each citing the passage
 * it comes from. Every draft is an ordinary proposed reading: it waits in the
 * review queue and is NOT in force until an approver accepts it. The person who
 * pressed the button is its proposer, so the two-person rule applies as usual.
 */
export function DraftWithPhibian({
  sourceId,
  onDrafted,
}: {
  sourceId: string;
  onDrafted?: (extractions: library.Extraction[]) => void;
}) {
  const queryClient = useQueryClient();
  // The same query and default as ProposeReading: the organization's administrators.
  const roles = useQuery({ queryKey: ['roles', 'list'], queryFn: () => users.roles() });
  const roleOptions = roles.data ?? [];
  const reviewer = (roleOptions.find((r) => r.key === 'org_admin') ?? roleOptions[0])?.id ?? '';

  const draft = useMutation({
    mutationFn: () => library.draftReadings(sourceId, { reviewerRoleId: reviewer }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      onDrafted?.(result.extractions);
    },
  });
  const result = draft.data;

  return (
    <div className="rn-col">
      <div className="rn-row kl-actions">
        <Button
          type="button"
          variant="secondary"
          disabled={reviewer === '' || draft.isPending}
          onClick={() => draft.mutate()}
        >
          {draft.isPending ? 'Phibian is reading the document…' : 'Draft rules with Phibian'}
        </Button>
        <span className="rn-muted">
          Phibian reads the whole document and drafts readings, each citing its passage. Each waits
          for review; none is in force until an approver accepts it.
        </span>
      </div>
      {result ? (
        <p role="status" className="rn-muted">
          {result.extractions.length === 0
            ? 'Phibian found no readings it could cite from this document.'
            : `Phibian drafted ${result.extractions.length === 1 ? '1 reading' : `${result.extractions.length} readings`}. They are waiting for review.`}
          {result.rejected.length > 0
            ? ` ${result.rejected.length} more ${result.rejected.length === 1 ? 'was' : 'were'} set aside because ${result.rejected.length === 1 ? 'it' : 'they'} did not cite the document exactly or were not well formed.`
            : ''}
        </p>
      ) : null}
      {draft.isError ? (
        <p className="rn-muted kl-error" role="alert">
          {draft.error instanceof Error && draft.error.message
            ? draft.error.message
            : 'Phibian could not draft readings just now. Nothing was saved.'}
        </p>
      ) : null}
    </div>
  );
}

/** Who drafted a reading, and the passage it cites. Nothing when a person wrote it. */
export function DraftedBy({ extraction }: { extraction: library.Extraction }) {
  const p = extraction.draftProvenance;
  if (!p) return null;
  return (
    <div className="rn-col">
      <p className="rn-muted">
        Drafted by Phibian ({p.modelId}) on {formatInstantDay(p.draftedAt)}
        {p.sourceVersionLabel ? `, from the ${p.sourceVersionLabel} version` : ''}. A person must
        still approve it.
      </p>
      <blockquote className="kl-excerpt">“{p.citation.text}”</blockquote>
      <p className="rn-muted">Why Phibian read it as a rule: {p.rationale}</p>
    </div>
  );
}
